import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import _debounce from 'lodash/debounce';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DropdownItemProps, StrictDropdownProps } from 'semantic-ui-react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Input,
  Loader,
} from 'semantic-ui-react';
import { useOnClickOutside } from 'usehooks-ts';

import { InfiniteScroll } from '@components/infinite-scroll';

import s from './search-dropdown.scss';

export interface SearchDropdownProps<
  T extends DropdownItemProps = DropdownItemProps,
> extends Omit<StrictDropdownProps, 'options' | 'loading' | 'onSearchChange'> {
  /**
   * The list of options to display
   */
  options?: T[];

  /**
   * Whether options are currently being loaded
   */
  loading?: boolean;

  /**
   * Whether there are more options available to load
   */
  hasMore?: boolean;

  /**
   * Callback to load (more) options. Called on first open and by infinite scroll.
   * When omitted, the dropdown renders with static options (no lazy loading).
   */
  onLoad?: () => void;

  /**
   * Placeholder text for the search input @default 'Search...'
   */
  searchPlaceholder?: string;

  /**
   * Callback when the search value changes. When provided, the caller is
   * responsible for filtering (server-side search). When omitted, options
   * are filtered client-side.
   */
  onSearchChange?: (query: string) => void;

  /**
   * Whether the dropdown should close after an option is selected.
   * When `false`, the menu stays open so users can pick several options
   * in a row; it still closes on outside click or Escape.
   *
   * @default true
   */
  closeOnSelect?: boolean;

  /**
   * Additional class name for the dropdown menu
   */
  menuClassName?: string;

  /**
   * Render each option. Defaults to a simple DropdownItem.
   */
  renderOption?: (option: T, props: { active: boolean }) => ReactNode;

  /**
   * The value of the currently selected option (used for active highlighting)
   */
  selectedValue?: string;

  /**
   * Called when an option is selected
   */
  onSelect?: (option: T) => void;

  /**
   * Message shown when the options list is empty (no `options` at all, or
   * all filtered out by an unmatched search). Defaults to
   * `'No options to display'`.
   */
  emptyMessage?: ReactNode;
}

/**
 * A Semantic UI Dropdown with an in-menu search input, lazy loading, and
 * infinite scroll. The search input is rendered inside the menu (fixed at
 * the top, above the scrollable option list) rather than replacing the
 * dropdown trigger text.
 *
 * The dropdown defers opening until the initial load completes, and uses
 * `closeOnBlur={false}` with an outside-click handler so that focusing
 * the search input doesn't trigger Semantic UI's blur-to-close behaviour.
 *
 * Any additional Semantic UI Dropdown props are forwarded to the underlying
 * Dropdown component — including `trigger`, which replaces the default
 * text-button trigger with any ReactNode (e.g. a `ToolbarButton`).
 */
export const SearchDropdown = <
  T extends DropdownItemProps = DropdownItemProps,
>({
  closeOnSelect = true,
  options = [] as T[],
  loading,
  hasMore,
  onLoad,
  searchPlaceholder = 'Search...',
  onSearchChange,
  menuClassName,
  renderOption,
  selectedValue,
  emptyMessage = 'No options to display',
  onSelect,
  ...dropdownProps
}: SearchDropdownProps<T>) => {
  useStyles(s);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  // Suppresses the close that SUI's Dropdown root fires via click-
  // bubbling after an item is selected. Outside-click and Escape close
  // pass through unaffected because they don't set this flag.
  const ignoreNextCloseRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [awaitingSearchResults, setAwaitingSearchResults] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchQuery || onSearchChange) return options;
    const lower = searchQuery.toLowerCase();
    return options.filter(opt =>
      String(opt.text ?? '')
        .toLowerCase()
        .includes(lower),
    );
  }, [options, onSearchChange, searchQuery]);

  const showSearch = options.length > 10 || !!searchQuery;

  const optionItems = filteredOptions.map(option => {
    const key = option.key ?? String(option.text);
    const active =
      selectedValue != null && String(option.text) === selectedValue;

    if (renderOption) {
      return renderOption(option, { active });
    }

    return (
      <DropdownItem
        {...option}
        key={key}
        active={active}
        onClick={() => {
          if (!closeOnSelect) ignoreNextCloseRef.current = true;
          onSelect?.(option);
        }}
      />
    );
  });

  // Open the menu once the initial load completes
  useEffect(() => {
    if (pendingOpen && !loading) {
      setPendingOpen(false);
      setIsOpen(true);
    }
  }, [pendingOpen, loading]);

  // Clear the search-loading state once results arrive
  useEffect(() => {
    if (awaitingSearchResults && !loading) {
      setAwaitingSearchResults(false);
    }
  }, [awaitingSearchResults, loading]);

  // Focus the search input when it becomes visible
  useEffect(() => {
    if (isOpen && showSearch) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, showSearch]);

  const debouncedSearchChange = useMemo(
    () =>
      _debounce(callback => {
        setAwaitingSearchResults(true);
        callback();
      }, 300),
    [],
  );

  const handleClose = useCallback(() => {
    if (ignoreNextCloseRef.current) {
      ignoreNextCloseRef.current = false;
      return;
    }
    setIsOpen(false);
    setPendingOpen(false);
    setSearchQuery('');
    setAwaitingSearchResults(false);
    debouncedSearchChange.cancel();
    onSearchChange?.('');
  }, [debouncedSearchChange, onSearchChange]);

  // Close on outside click. Needed because we use closeOnBlur={false}
  // so that focusing the search input doesn't close the dropdown.
  useOnClickOutside(dropdownRef as React.RefObject<HTMLElement>, () => {
    if (!isOpen) return;
    handleClose();
  });

  // Hide Semantic UI's default caret when a custom trigger is supplied — the
  // trigger owns its own visual, and Semantic's default icon otherwise renders
  // outside the trigger's bounds.
  const icon = dropdownProps.trigger != null ? null : dropdownProps.icon;

  return (
    <Dropdown
      {...dropdownProps}
      ref={dropdownRef}
      className={classNames(
        'darkreader-ignore',
        s.root,
        pendingOpen ? s.loading : '',
        dropdownProps.className,
      )}
      icon={icon}
      open={isOpen}
      closeOnBlur={false}
      loading={pendingOpen}
      onOpen={() => {
        if (onLoad && !loading && !options.length) {
          onLoad();
          setPendingOpen(true);
        } else {
          setIsOpen(true);
        }
      }}
      onClose={handleClose}
    >
      <DropdownMenu className={classNames(s.menu, menuClassName)}>
        {showSearch && (
          <div className={s.searchContainer}>
            <Input
              ref={searchInputRef}
              className={s.searchInput}
              icon="search"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Escape') return;
                e.stopPropagation();
                if (
                  e.key === 'Enter' &&
                  !awaitingSearchResults &&
                  filteredOptions.length === 1
                ) {
                  // Click the first child of the options list so Link
                  // navigation fires.
                  const item = optionsListRef.current
                    ?.firstElementChild as HTMLElement | null;
                  item?.click();
                }
              }}
              onChange={(_, { value }) => {
                setSearchQuery(value);
                if (onSearchChange)
                  debouncedSearchChange(() => onSearchChange(value));
              }}
            />
          </div>
        )}
        <div ref={optionsListRef} className={s.optionsList}>
          {awaitingSearchResults && loading ? (
            <Loader className={s.loader} active inline="centered" />
          ) : optionItems.length > 0 ? (
            onLoad ? (
              <InfiniteScroll
                hasMore={hasMore && !(!onSearchChange && searchQuery)}
                isLoading={loading}
                loadMore={onLoad}
                hasScrollableParent
              >
                {optionItems}
              </InfiniteScroll>
            ) : (
              optionItems
            )
          ) : loading ? (
            <Loader className={s.loader} active inline="centered" />
          ) : (
            <div className={s.emptyMessage}>
              {searchQuery ? 'No results found' : emptyMessage}
            </div>
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default SearchDropdown;
